import React, { useEffect, useState } from 'react';
import { Stack, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import dayjs, { Dayjs } from 'dayjs';
import CustomDatePicker from './InputDate';

type Props = {
  value?: string | null;
  onSave: (newValue: string | null) => Promise<void> | void;
  disabled?: boolean;
  hasTime?: boolean;
};

export const EditableDateCell: React.FC<Props> = ({ value, onSave, disabled = false, hasTime }) => {
  const [isEdit, setIsEdit] = useState(false);
  const [currentValue, setCurrentValue] = useState<Dayjs | null>(value ? dayjs(value) : null);
  const [originValue, setOriginValue] = useState<Dayjs | null>(value ? dayjs(value) : null);

  useEffect(() => {
    setCurrentValue(value ? dayjs(value) : null);
    setOriginValue(value ? dayjs(value) : null);
  }, [value]);

  const stopRowClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const handleEdit = (e: React.MouseEvent) => {
    stopRowClick(e);
    if (disabled) return;
    setIsEdit(true);
  };

  const handleCancel = (e: React.MouseEvent) => {
    stopRowClick(e);
    setCurrentValue(originValue);
    setIsEdit(false);
  };

  const handleSave = async (e: React.MouseEvent) => {
    stopRowClick(e);

    const confirmed = window.confirm('Are you sure you want to update this date?');
    if (!confirmed) return;

    const formattedValue = currentValue ? currentValue.toISOString() : null;

    await onSave(formattedValue);
    setOriginValue(currentValue);
    setIsEdit(false);
  };

  return (
    <Stack alignItems={'center'} justifyContent={'center'} height={'100%'}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
        {/* Date picker – chặn click bubbling */}
        <div onMouseDown={stopRowClick} onClick={stopRowClick}>
          <CustomDatePicker
            hasTime={hasTime}
            value={currentValue}
            onChange={(date) => setCurrentValue(date)}
            title=""
            noPlaceholder
            datePickerProps={{
              disabled: !isEdit || disabled,
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  border: 'none',
                  color: '#000',
                  height: '36px',
                  overflow: 'hidden',
                  '&.Mui-error fieldset': {
                    borderColor: 'red'
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#000',
                    color: '#000'
                  }
                },
                '& .MuiInputBase-root': {
                  '&.Mui-disabled': {
                    color: '#000',
                    WebkitTextFillColor: '#000'
                  }
                }
              }
            }}
          />
        </div>

        {/* Icons */}
        {!isEdit ? (
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            onMouseDown={stopRowClick}
            onClick={handleEdit}
            disabled={disabled}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : (
          <>
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              onMouseDown={stopRowClick}
              onClick={handleSave}>
              <SaveIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              onMouseDown={stopRowClick}
              onClick={handleCancel}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default EditableDateCell;
